import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import inventoryService from '../../services/inventoryService';
import { Alert, Button, Spinner } from '../UI/Index';
import InventoryList from './InventoryList';
import Pagination from '../UI/Pagination';
import CreateInventoryModal from '../Inventory/CreateInventoryModal';
import { useAuth } from '../../context/AuthContext';


const PAGE_SIZE = 25;

const Dashboard = () => {
  const [inventories, setInventories] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // La página vive en la URL (?page=N) para conservarla al volver del detalle
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(parseInt(searchParams.get('page'), 10) || 1, 1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [recordType, setRecordType] = useState('inventory');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInventories = async () => {
      try {
        setLoading(true);
        const response = await inventoryService.getInventories({ page, pageSize: PAGE_SIZE });

        if (response.success) {
          // Página fuera de rango (p. ej. URL antigua): saltar a la última
          if (response.pagination && page > response.pagination.totalPages) {
            const last = response.pagination.totalPages;
            setSearchParams(last > 1 ? { page: String(last) } : {}, { replace: true });
            return;
          }
          setInventories(response.data || []);
          setPagination(response.pagination || null);
        } else {
          setError(response.message || 'Error al cargar inventarios');
        }
      } catch (err) {
        setError(err.message || 'Error al cargar inventarios');
      } finally {
        setLoading(false);
      }
    };

    fetchInventories();
  }, [page, reloadKey, setSearchParams]);

  const { roles } = useAuth();

  const handleOpenCreateModal = (type) => {
    setRecordType(type);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const goToPage = (newPage) => {
    setSearchParams(newPage > 1 ? { page: String(newPage) } : {});
    // En móvil el paginador queda al final de la lista: volver arriba
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Tras crear, volver a la página 1 para ver el inventario nuevo
  const handleInventoryCreated = () => {
    setShowCreateModal(false);
    goToPage(1);
    setReloadKey((k) => k + 1);
  };

  const handleViewInventory = (inventoryId) => {
    navigate(`/inventory/${inventoryId}`, { state: { dashboardSearch: searchParams.toString() } });
  };


  return (
    <div className="container mx-auto">
      <div className="items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <div className="flex item s-center gap-2">
          <Button type="primary" onClick={() => handleOpenCreateModal('inventory')}>
            <b>Crear inventario</b>
          </Button>
          {!roles.isAdmin && (
            <Button type="primary" className="bg-orange-500 hover:bg-orange-600" onClick={() => handleOpenCreateModal('expired')}>
              <b>Crear Caducados</b>
            </Button>)
          }
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div className="p-4 bg-white rounded-lg shadow-md">
        <h2 className="mb-4 text-xl font-semibold">Inventarios</h2>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : inventories.length === 0 ? (
          <div className="py-4 text-center text-gray-500">
            No hay inventarios disponibles.
          </div>
        ) : (
          <>
            <InventoryList
              inventories={inventories}
              onViewInventory={handleViewInventory}
            />
            {pagination && (
              <Pagination
                page={pagination.page}
                pageSize={pagination.pageSize}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={goToPage}
              />
            )}
          </>
        )}
      </div>

      {showCreateModal && (
        <CreateInventoryModal
          isOpen={showCreateModal}
          onClose={handleCloseCreateModal}
          onInventoryCreated={handleInventoryCreated}
          recordType={recordType}
        />
      )}
    </div>
  );
};

export default Dashboard;